import { ChangeEvent, useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';

import type { NextPage } from 'next';

import { FormControl, FormControlLabel, Radio, RadioGroup, TextareaAutosize, Typography } from '@mui/material';

import { useRecoilValue } from 'recoil';

import { useMediationAgreementReports } from '@hooks_iqfu/dashboard/useMediationAgreementReport';

import { outputAlertMessage } from '@utils_iqfu/outputAlertMessage';

import DatePickerWrapper from '@components_iqloud/form/DatePickerWrapper';
import SelectBoxWrapper from '@components_iqloud/form/SelectBoxWrapper';

import ConsoleChatPageContainer from '@components_iqfu/chat/common/chat_page_container/ConsoleChatPageContainer';
import ConsoleChatMainContainer from '@components_iqfu/chat/common/main_container/ConsoleChatMainContainer';
import RequiredLabel from '@components_iqfu/dashboard/required_label/RequiredLabel';
import UploadPdfArea from '@components_iqfu/dashboard/upload_files/UploadPdfArea';

import { companyLoginUserDataSelector } from '@recoil_iqfu/atoms/auth/companyLoginUserData';

import {
  MediationAgreementReportsCategory,
  MediationAgreementReportsCategoryKey,
} from '@type-def_iqfu/MediationAgreementReportsType';

import { NotificationText } from '@constants_iqfu';
import { useNotificationContext } from '@contexts_common/notificationContext';
import styles from '@styles_iqfu/chat/mediation_agreement_reports.module.scss';
import { convertPrice } from '@utils_common/convertPrice';
import { handleInputText } from '@utils_common/handleInputText';

const MediationAgreementReportsCreate: NextPage = () => {
  const { createMediationAgreementReport } = useMediationAgreementReports();
  const { setNotificationDisplay, setNotificationMessage, setNotificationStatus } = useNotificationContext();
  const router = useRouter();

  // クエリパラメータからchat_room_id / offline_seller_lead_id / seller_nameを取得
  const { chat_room_id, offline_seller_lead_id, seller_name: querySellerName } = router.query;
  const chatRoomId = chat_room_id ? Number(chat_room_id) : null;
  const offlineSellerLeadId = offline_seller_lead_id ? Number(offline_seller_lead_id) : null;

  const { companyId, isMultipleCompanyUser, multipleCompaniesOptions } = useRecoilValue(companyLoginUserDataSelector);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [disabled, setDisabled] = useState(true);
  const [sellerName, setSellerName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [contractAmount, setContractAmount] = useState<number | null>(null);
  const [category, setCategory] = useState<MediationAgreementReportsCategoryKey>('non_exclusive');
  const [effectiveDate, setEffectiveDate] = useState<any>(null);
  const [expiryDate, setExpiryDate] = useState<any>(null);
  const [mediationAgreements, setMediationAgreements] = useState<Blob[]>([]);
  const [note, setNote] = useState('');

  const submitUser = async () => {
    setDisabled(false);
    const formData = new FormData();
    if (isMultipleCompanyUser) {
      selectedCompany && formData.append('company_id', String(selectedCompany));
    } else {
      formData.append('company_id', String(companyId));
    }
    if (contractAmount !== null) formData.append('contract_amount', String(contractAmount * 10000));
    formData.append('seller_name', sellerName);
    formData.append('owner_name', ownerName);
    formData.append('category', category);
    formData.append('effective_date', effectiveDate);
    formData.append('expiry_date', expiryDate);

    if (mediationAgreements.length > 0) {
      mediationAgreements.forEach((file) => {
        formData.append('files[][file]', file);
      });
    }
    formData.append('note', note);

    if (offlineSellerLeadId) {
      formData.append('reportable_type', 'OfflineSeller::Lead');
      formData.append('reportable_id', String(offlineSellerLeadId));
    } else if (chatRoomId) {
      formData.append('chat_room_id', String(chatRoomId));
    }

    const result = await createMediationAgreementReport(formData);

    if (result.id) {
      setNotificationStatus('success');
      setNotificationMessage(NotificationText.successCreateMediationAgreementReport);
      setNotificationDisplay(true);
      router.push(`/console/company/chat/mediation_agreement_reports`);
    } else {
      setNotificationStatus('alert');
      setNotificationMessage(outputAlertMessage(result));
      setNotificationDisplay(true);
    }
    setDisabled(false);
  };
  const removeFileHandler = (index: number) => {
    setMediationAgreements((prev) => prev.filter((_, i) => i !== index));
  };

  // クエリパラメータから売主名をプリフィル
  useEffect(() => {
    if (!isInitialized && querySellerName && typeof querySellerName === 'string') {
      setSellerName(querySellerName);
      setIsInitialized(true);
    }
  }, [querySellerName, isInitialized]);

  useEffect(() => {
    if (
      sellerName === '' ||
      effectiveDate === null ||
      expiryDate === null ||
      contractAmount === null ||
      mediationAgreements.length === 0 ||
      (isMultipleCompanyUser && selectedCompany === null)
    ) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [
    sellerName,
    effectiveDate,
    expiryDate,
    mediationAgreements,
    contractAmount,
    isMultipleCompanyUser,
    selectedCompany,
  ]);

  return (
    <>
      <ConsoleChatPageContainer userType="company" limitHeightToWindowHeight={false}>
        <ConsoleChatMainContainer>
          <div className={styles.page_container}>
            <div className={styles.title_wrap}>
              <h3 className={styles.section_title}>媒介契約報告</h3>
            </div>

            <div className={styles.report_lead}>
              <span className={styles.text_red}>イクラ不動産よりご紹介した顧客様</span>
              と媒介契約を締結されましたら、必ずこちらのフォームから1週間以内にご報告が必要です。　
              <br />
              売買契約を締結されましたら、
              <Link href={'/console/company/chat/agreement_reports/create'} passHref>
                <a className={styles.link_text}>売買契約報告</a>
              </Link>
              から再度ご報告下さい。
            </div>

            {isMultipleCompanyUser && (
              <div className={styles.input_item}>
                <label className={styles.input_item_label}>
                  会社名
                  <RequiredLabel />
                </label>
                <SelectBoxWrapper
                  selectList={multipleCompaniesOptions}
                  state={selectedCompany}
                  setState={setSelectedCompany}
                  placeholder="会社選択"
                  size="width1"
                />
              </div>
            )}

            <div className={styles.input_item}>
              <div className={styles.input_item_label} style={{ maxWidth: '240px' }}>
                <div>
                  売主名
                  <RequiredLabel />
                </div>
                <div className={`${styles.input_item_label} ${styles.input_item_label_note}`}>
                  ※イクラ不動産からの売却相談依頼時の売主名をご入力ください
                </div>
              </div>

              <input
                className={styles.input_text_width1}
                type="text"
                maxLength={30}
                placeholder="売主名"
                value={sellerName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputText(event, setSellerName);
                }}
              />
            </div>

            <div className={styles.input_item}>
              <div className={styles.input_item_label} style={{ maxWidth: '240px' }}>
                <div>名義人名</div>
                <div className={`${styles.input_item_label} ${styles.input_item_label_note}`}>
                  ※イクラ不動産からの​売却相談依頼時の​売主様と​名義人が​異なる​場合のみ​ご入力ください​
                </div>
              </div>
              <input
                className={styles.input_text_width1}
                type="text"
                maxLength={30}
                placeholder="名義人名"
                value={ownerName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  handleInputText(event, setOwnerName);
                }}
              />
            </div>

            <div className={styles.input_item}>
              <h2 className={styles.input_item_label}>媒介契約の種類</h2>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="row-radio-buttons-group"
                  className={`${styles.radio} ${styles.radio_group_width1}`}
                  value={category}
                  onChange={(event) => {
                    setCategory(event.target.value as MediationAgreementReportsCategoryKey);
                  }}
                >
                  <FormControlLabel
                    value={'non_exclusive'}
                    control={<Radio />}
                    label={MediationAgreementReportsCategory.non_exclusive}
                  />
                  <FormControlLabel
                    value={'exclusive'}
                    control={<Radio />}
                    label={MediationAgreementReportsCategory.exclusive}
                  />
                  <FormControlLabel
                    value={'privileged_and_exclusive'}
                    control={<Radio />}
                    label={MediationAgreementReportsCategory.privileged_and_exclusive}
                  />
                </RadioGroup>
              </FormControl>
            </div>

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>
                締結日
                <RequiredLabel />
              </label>
              <DatePickerWrapper
                state={effectiveDate}
                setState={setEffectiveDate}
                inputType="day"
                outputType="day"
                size="width1"
              />
            </div>

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>
                媒介価格
                <RequiredLabel />
              </label>
              <input
                className={styles.input_text_width1}
                type="text"
                maxLength={30}
                placeholder="媒介価格"
                value={contractAmount ?? ''}
                onChange={(event) => {
                  const value = event.target.value;
                  setContractAmount(convertPrice(value));
                }}
              />
              <span className={styles.contract_amount_unit}>万円</span>
            </div>

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>
                有効期限
                <RequiredLabel />
              </label>
              <DatePickerWrapper
                state={expiryDate}
                setState={setExpiryDate}
                inputType="day"
                outputType="day"
                size="width1"
              />
            </div>

            <div className={styles.input_item}>
              <div className={styles.input_item_label}>
                <div>
                  媒介契約書
                  <RequiredLabel />
                </div>
              </div>
              <div className={styles.upload_area}>
                <UploadPdfArea
                  files={mediationAgreements}
                  setFiles={setMediationAgreements}
                  fileCategory={'媒介契約書'}
                  multipleUploadAnnotation={true}
                >
                  <Typography sx={{ fontSize: '1.6rem', mt: 1 }}>全てのページをアップロードしてください</Typography>
                </UploadPdfArea>
                {mediationAgreements.length > 0 &&
                  mediationAgreements.map((file, index) => (
                    <div key={`${(file as any).name}-${index}`} className={styles.attached_file_wrap}>
                      {(file as any).name}
                      <button className={styles.delete_button} onClick={() => removeFileHandler(index)}>
                        削除
                      </button>
                    </div>
                  ))}
              </div>
            </div>

            <div className={styles.input_item}>
              <label className={styles.input_item_label}>備考</label>
              <TextareaAutosize
                value={note}
                minRows={3}
                placeholder="特記事項などございましたら、こちらに入力してください。"
                maxLength={200}
                className={styles.input_text_area}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
                  setNote(event.target.value);
                }}
              />
            </div>

            <button
              className={`${styles.action_button} ${disabled && styles.inactive}`}
              type="submit"
              disabled={disabled}
              onClick={submitUser}
            >
              保存
            </button>
          </div>
        </ConsoleChatMainContainer>
      </ConsoleChatPageContainer>
    </>
  );
};

export default MediationAgreementReportsCreate;
